import { createError, getTaxYearMonthYearPeriod, MonthYearPeriod, toMonthYearPeriod } from '@common/neo.tax';
import { useMemo } from 'react';

import { ProductType } from '@neo.tax/shared/graphql';
import { useAllFiscalYears, useCurrentCompany } from '@neo.tax/shared/hooks';

export type AvailableFiscalYearOption = {
  year: number;
  id: string | undefined; // no id if no fiscal year exists for the year
  fiscalYearPeriod: MonthYearPeriod;
};

export type AvailableProducts = Record<ProductType, AvailableFiscalYearOption[]>;

export const useAvailableProducts = ():
  | {
      availableProducts: AvailableProducts;
      loading: true;
    }
  | {
      availableProducts: AvailableProducts;
      loading: false;
    } => {
  const { company } = useCurrentCompany();
  const { fiscalYears } = useAllFiscalYears();
  return useMemo(() => {
    if (!fiscalYears || !company) {
      return {
        availableProducts: { [ProductType.UsAsc]: [], [ProductType.UsRd]: [], [ProductType.CaSred]: [] },
        loading: true,
      };
    }

    if (typeof company.businessQuestions.fiscalYearEndMonth !== 'number') {
      throw createError('MissingFiscalYearEndMonthError', 'Fiscal year end month is missing');
    }
    const fiscalYearEndMonth = company.businessQuestions.fiscalYearEndMonth;
    if (fiscalYears.length === 0) {
      const currentMonth = new Date().getUTCMonth();
      const currentYear = new Date().getUTCFullYear();
      // To get the most recent closed tax year working with a fiscal year end month of December
      // 1. We always subtract 1 from the current year
      // Current Month Year   Fiscal Year End Month   Most Recent Closed Tax Year
      // March 2023           December                2022 (current year - 1)
      // December 2023        December                2022 (current year - 1)
      // January 2024         December                2023 (current year - 1)

      // To get the most recent closed tax year working with a fiscal year end month other than December
      // 1. When current month <= fiscal year end month, we always subtract 2 from the current year
      // 2. When current month > fiscal year end month, we always subtract 1 from the current year
      // Current Month Year   Fiscal Year End Month   Most Recent Closed Tax Year
      // July 2022            June                    2021 (current year - 1)
      // December 2022        June                    2021 (current year - 1)
      // March 2023           June                    2021 (current year - 2)
      // June 2023            June                    2021 (current year - 2)
      // July 2023            June                    2022 (current year - 1)
      // November 2023        June                    2022 (current year - 1)
      const mostRecentClosedTaxYear =
        fiscalYearEndMonth === 11 || currentMonth > fiscalYearEndMonth ? currentYear - 1 : currentYear - 2;

      const fiscalYearPeriod = getTaxYearMonthYearPeriod(fiscalYearEndMonth, mostRecentClosedTaxYear);

      return {
        availableProducts: {
          [ProductType.UsAsc]: [],
          [ProductType.UsRd]: [{ year: mostRecentClosedTaxYear, id: undefined, fiscalYearPeriod }],
          [ProductType.CaSred]: [],
        },
        loading: false,
      };
    }

    const sortedFiscalYears = [...fiscalYears].sort((a, b) => b.year - a.year);
    const products = sortedFiscalYears.reduce(
      (acc: AvailableProducts, fiscalYear) => {
        const fiscalYearPeriod = toMonthYearPeriod(
          new Date(fiscalYear.taxYearStartDate),
          new Date(fiscalYear.taxYearEndDate),
        );
        if (!fiscalYear.enabledDatetime) {
          return acc;
        }
        acc[fiscalYear.productType].push({ year: fiscalYear.year, id: fiscalYear.id, fiscalYearPeriod });
        return acc;
      },
      { [ProductType.UsAsc]: [], [ProductType.UsRd]: [], [ProductType.CaSred]: [] },
    );

    return {
      availableProducts: products,
      loading: false,
    };
  }, [company, fiscalYears]);
};
